import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { UserAgent } from "../../../utilities/UserAgent";
import { DesktopView } from "../views/DesktopBuilderView";
import { MainDrawer } from "../drawer/mainDrawer";
import { BuilderDrawerContext } from "../drawer/useBuilderDrawer";
import { DrawerContent, toDrawerContent } from "../../../entities/DrawerContent";
import { EventTracking } from "../../../utilities/Tracking/EventTracking";
import { useBuilder } from "../useBuilder";
import { useEditor } from "../editor/useEditor";
import Hidden from "@material-ui/core/Hidden";
import { MobileLayout } from "./MobileLayout";
import {useOfflineAssets} from '../../offline/useOfflineAssets';
import { useLocation } from "react-router";
import { useIsMobile } from "../../../components/Mui3WidthProvider";
import { makeStyles } from "@material-ui/core";

const drawerWidth = 350;

const useStyles = makeStyles((theme) => ({
	moveableContent: {
		display: "flex",
		flexGrow: 1,
		[theme.breakpoints.up("md")]: {
			transition: theme.transitions.create("margin", {
				easing: theme.transitions.easing.sharp,
				duration: theme.transitions.duration.leavingScreen,
			}),
			marginRight: 0,
		},
	},
	moveableContentShift: {
		[theme.breakpoints.up("md")]: {
			transition: theme.transitions.create("margin", {
				easing: theme.transitions.easing.easeOut,
				duration: theme.transitions.duration.enteringScreen,
			}),
			marginRight: drawerWidth,
		},
	},
}));

const getStoredDrawerOpenState = (preview:boolean, isMobile:boolean): boolean => {
	if(preview){
		return !isMobile;
	}

	const drawerStatus = localStorage.getItem("drawerStatus");
	return drawerStatus ? drawerStatus === "1" : false;
};

const getStoredDrawerContent = (preview:boolean): DrawerContent => {
	if(preview){
		return DrawerContent.Slides;
	}
	const drawerContent = localStorage.getItem("drawerType");
	return drawerContent ? toDrawerContent(drawerContent) : DrawerContent.Details;
};

const geniusBadgeDismisalCheck = (assetId: string) => {
	const hideBadgeKey = `sermonGeniusBadge-${assetId}`;
	return localStorage.getItem(hideBadgeKey) != null;
};

interface Props {
	onCloseContentDrawer: () => void;
}

export function Builder(props: Props) {
	const { builderAsset } = useBuilder();
	const { wordCount } = useEditor();
	const { onlineMode: online } = useOfflineAssets();
	const location = useLocation();
	const isPreview = location.pathname.includes('builderpreview');
	const isMobile = useIsMobile();
	const classes = useStyles();

	const [drawerOpen, setMainDrawerOpen] = useState(getStoredDrawerOpenState(isPreview, isMobile));
	const [drawerContent, setMainDrawerContent] = useState<DrawerContent>(getStoredDrawerContent(isPreview));

	const [geniusBadgeDismissed, setGeniusBadgeDismissed] = useState(geniusBadgeDismisalCheck(builderAsset.id));
	const [showGeniusBadge, setShowGeniusBadge] = useState(!geniusBadgeDismissed);

	useEffect(() => window.localStorage.setItem("drawerStatus", drawerOpen ? "1" : "0"), [drawerOpen]);
	useEffect(() => window.localStorage.setItem("drawerType", drawerContent), [drawerContent]);

	useEffect(() => {
		if (!geniusBadgeDismissed) {
			setShowGeniusBadge(wordCount > 100);
		} else {
			setShowGeniusBadge(false);
		}
	}, [wordCount, geniusBadgeDismissed]);

	const dismissGeniusBadge = () => {
		const hideBadgeKey = `sermonGeniusBadge-${builderAsset.id}`;
		localStorage.setItem(hideBadgeKey, JSON.stringify(true));
		setGeniusBadgeDismissed(true);
	};

	const onMainDrawerChange = (open: boolean, contentType?: DrawerContent) => {
		setMainDrawerOpen(open);

		if (open) {
			props.onCloseContentDrawer();
			if (contentType) {
				EventTracking.Builder.onDrawerOpen(contentType);
			}
		}

		if (contentType) {
			setMainDrawerContent(contentType);
		}
	};

	return (
		<BuilderDrawerContext.Provider
			value={{
				drawerOpen,
				drawerContent,
				onMainDrawerChange,
				showGeniusBadge,
				dismissGeniusBadge,
			}}
		>
			<div style={{ height: "100%" }}>
				<div
					className={classNames(classes.moveableContent, {
						[classes.moveableContentShift]: drawerOpen && online,
					})}
				>
					<BuilderView />
				</div>
				<MainDrawer open={online ? drawerOpen : false} onDrawerToggle={onMainDrawerChange} contentType={drawerContent} />
			</div>
		</BuilderDrawerContext.Provider>
	);
}

function BuilderView() {
	const [iOS] = useState(UserAgent.iOS());

	if (iOS) {
		return <MobileLayout type="ios" />;
	}

	return (
		<>
			<Hidden smDown>
				<DesktopView />
			</Hidden>
			<Hidden mdUp>
				<MobileLayout type="android" />
			</Hidden>
		</>
	);
}